import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Upload, FileText, X, Camera, Image as ImageIcon } from 'lucide-react';
import {
  legalDocumentSchema,
  type LegalDocumentInput,
  type LegalDocument,
  type LegalDocumentCategory,
  documentTypes,
  documentTypeLabels,
  documentStatuses,
  documentStatusLabels,
  confidentialityLevels,
  confidentialityLabels,
} from '@/schemas/legalDocumentSchemas';
import { useCreateLegalDocument, useUpdateLegalDocument } from '@/hooks/useLegalDocuments';
import { FolderSelector } from '@/components/LegalDocuments/FolderManager';

interface DocumentUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  document?: LegalDocument | null;
  categories: LegalDocumentCategory[];
  selectedFolderId?: string | null;
}

export function DocumentUploadDialog({
  open,
  onOpenChange,
  document,
  categories,
  selectedFolderId: initialFolderId,
}: DocumentUploadDialogProps) {
  const isEditing = !!document;
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [folderId, setFolderId] = useState<string | null>(initialFolderId || null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const createDocument = useCreateLegalDocument();
  const updateDocument = useUpdateLegalDocument();

  const form = useForm<LegalDocumentInput>({
    resolver: zodResolver(legalDocumentSchema),
    defaultValues: {
      title: '',
      code: '',
      description: '',
      category_id: null,
      document_type: 'other',
      file_url: null,
      file_name: null,
      file_size: null,
      file_type: null,
      version: '1.0',
      effective_date: null,
      expiration_date: null,
      status: 'draft',
      issued_by: null,
      review_date: null,
      tags: [],
      keywords: null,
      confidentiality_level: 'internal',
      notes: null,
    },
  });

  useEffect(() => {
    if (document) {
      form.reset({
        title: document.title,
        code: document.code || '',
        description: document.description || '',
        category_id: document.category_id,
        document_type: document.document_type,
        file_url: document.file_url,
        file_name: document.file_name,
        file_size: document.file_size,
        file_type: document.file_type,
        version: document.version,
        effective_date: document.effective_date,
        expiration_date: document.expiration_date,
        status: document.status,
        issued_by: document.issued_by,
        review_date: document.review_date,
        tags: document.tags || [],
        keywords: document.keywords,
        confidentiality_level: document.confidentiality_level,
        notes: document.notes,
      });
      setFolderId((document as any).folder_id || null);
    } else {
      form.reset({
        title: '',
        code: '',
        description: '',
        category_id: null,
        document_type: 'other',
        file_url: null,
        file_name: null,
        file_size: null,
        file_type: null,
        version: '1.0',
        effective_date: null,
        expiration_date: null,
        status: 'draft',
        issued_by: null,
        review_date: null,
        tags: [],
        keywords: null,
        confidentiality_level: 'internal',
        notes: null,
      });
      setSelectedFile(null);
      setPreviewUrl(null);
      setFolderId(initialFolderId || null);
    }
  }, [document, open, initialFolderId]);

  // Limpiar preview URL al desmontar
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = (file: File) => {
    setSelectedFile(file);
    form.setValue('file_name', file.name);
    form.setValue('file_size', file.size);
    form.setValue('file_type', file.type);
    // En producción, aquí subirías el archivo a un storage y obtendrías la URL
    // Por ahora, usamos una URL placeholder
    form.setValue('file_url', `/uploads/documents/${file.name}`);

    // Crear preview si es imagen
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    form.setValue('file_url', null);
    form.setValue('file_name', null);
    form.setValue('file_size', null);
    form.setValue('file_type', null);
    // Reset inputs
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const isImageFile = (file: File | null, fileType: string | null) => {
    if (file) return file.type.startsWith('image/');
    if (fileType) return fileType.startsWith('image/');
    return false;
  };

  const onSubmit = async (data: LegalDocumentInput) => {
    try {
      const dataWithFolder = { ...data, folder_id: folderId };
      if (isEditing && document) {
        await updateDocument.mutateAsync({ id: document.id, data: dataWithFolder });
      } else {
        await createDocument.mutateAsync(dataWithFolder as any);
      }
      onOpenChange(false);
    } catch (error) {
      // Error handled by the mutation
    }
  };

  const isLoading = createDocument.isPending || updateDocument.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {isEditing ? 'Editar Documento' : 'Nuevo Documento'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Modifique la información del documento'
              : 'Complete la información para agregar un nuevo documento'}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Título y Código */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>Título *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Ej: Certificación ISO 9001:2015" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Código</FormLabel>
                      <FormControl>
                        <Input {...field} value={field.value || ''} placeholder="DOC-001" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Tipo y Categoría */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="document_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo de Documento *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccione tipo" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {documentTypes.map((type) => (
                            <SelectItem key={type} value={type}>
                              {documentTypeLabels[type]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="category_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Categoría</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || undefined}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccione categoría" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Carpeta */}
              <FolderSelector value={folderId} onChange={setFolderId} />

              {/* Descripción */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descripción</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        value={field.value || ''}
                        placeholder="Descripción del documento..."
                        rows={3}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Archivo */}
              <div className="space-y-2">
                <FormLabel>Archivo o Imagen</FormLabel>
                <div className="border-2 border-dashed rounded-lg p-4">
                  {selectedFile || form.getValues('file_name') ? (
                    <div className="space-y-3">
                      {/* Preview de imagen */}
                      {(previewUrl || (form.getValues('file_url') && isImageFile(selectedFile, form.getValues('file_type')))) && (
                        <div className="flex justify-center">
                          <img
                            src={previewUrl || form.getValues('file_url') || ''}
                            alt="Preview"
                            className="max-h-48 rounded-lg object-contain border"
                          />
                        </div>
                      )}
                      {/* Info del archivo */}
                      <div className="flex items-center justify-between gap-3 p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          {isImageFile(selectedFile, form.getValues('file_type')) ? (
                            <ImageIcon className="w-8 h-8 text-primary flex-shrink-0" />
                          ) : (
                            <FileText className="w-8 h-8 text-primary flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className="font-medium truncate">
                              {selectedFile?.name || form.getValues('file_name')}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {selectedFile
                                ? `${(selectedFile.size / 1024).toFixed(1)} KB`
                                : form.getValues('file_size')
                                  ? `${(form.getValues('file_size')! / 1024).toFixed(1)} KB`
                                  : ''}
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={clearFile}
                          className="flex-shrink-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Instrucciones */}
                      <div className="text-center">
                        <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">
                          Sube un archivo o toma una foto
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          PDF, DOC, DOCX, XLS, XLSX, JPG, PNG (máx. 10MB)
                        </p>
                      </div>

                      {/* Botones de acción */}
                      <div className="flex flex-col sm:flex-row gap-2 justify-center">
                        {/* Botón subir archivo */}
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          className="gap-2"
                        >
                          <Upload className="w-4 h-4" />
                          Subir archivo
                        </Button>

                        {/* Botón cámara - Solo visible en móvil o con cámara */}
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => cameraInputRef.current?.click()}
                          className="gap-2"
                        >
                          <Camera className="w-4 h-4" />
                          Tomar foto
                        </Button>
                      </div>

                      {/* Input oculto para archivos */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp"
                        onChange={handleFileChange}
                      />

                      {/* Input oculto para cámara */}
                      <input
                        ref={cameraInputRef}
                        type="file"
                        className="hidden"
                        accept="image/*"
                        capture="environment"
                        onChange={handleFileChange}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Estado y Versión */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Estado</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccione estado" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {documentStatuses.map((status) => (
                            <SelectItem key={status} value={status}>
                              {documentStatusLabels[status]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="version"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Versión</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="1.0" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confidentiality_level"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confidencialidad</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Nivel" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {confidentialityLevels.map((level) => (
                            <SelectItem key={level} value={level}>
                              {confidentialityLabels[level]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Fechas */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="effective_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Fecha de Vigencia</FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          {...field}
                          value={field.value || ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="expiration_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Fecha de Vencimiento</FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          {...field}
                          value={field.value || ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="review_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Fecha de Revisión</FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          {...field}
                          value={field.value || ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Emitido por */}
              <FormField
                control={form.control}
                name="issued_by"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Emitido por</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ''}
                        placeholder="Ej: Bureau Veritas, AENOR, etc."
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Notas */}
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notas</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        value={field.value || ''}
                        placeholder="Notas adicionales..."
                        rows={2}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={form.handleSubmit(onSubmit)}
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEditing ? 'Guardar Cambios' : 'Crear Documento'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
